/**
 * Platform Detection Utilities
 *
 * Detects the user's device platform (iOS, Android, or Web) to determine
 * which app store URL to use (App Store vs Play Store).
 * Same logic as HomePage so store links behave consistently everywhere.
 */

export type Platform = 'ios' | 'android' | 'web';

/** App Store URL (same as HomePage) */
export const APP_STORE_URL = 'https://apps.apple.com/us/app/virtoly/id6758582538';
/** Play Store URL (same as HomePage) */
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.virtoly.app';

/**
 * Detect the user's platform (same check as HomePage isIOS/isAndroid).
 * - iOS: iPhone, iPad, iPod, or iPadOS 13+ (MacIntel + touch)
 * - Android: userAgent contains Android
 */
export const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') {
    return 'web';
  }

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(navigator.userAgent);

  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'web';
};

/**
 * Get the store URL for the current device (same URLs and same check as HomePage).
 * Use this when opening the app store (e.g. force-update modal, download buttons).
 */
export const getStoreUrlForCurrentDevice = (): string | null => {
  const platform = detectPlatform();
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return null;
};

/**
 * Check if the user is on a mobile device (iOS or Android)
 * 
 * @returns true if on iOS or Android, false otherwise
 */
export const isMobileDevice = (): boolean => {
  const platform = detectPlatform();
  return platform === 'ios' || platform === 'android';
};

/**
 * Check if the user is on iOS
 * 
 * @returns true if on iOS, false otherwise
 */
export const isIOSDevice = (): boolean => {
  return detectPlatform() === 'ios';
};

/**
 * Check if the user is on Android
 * 
 * @returns true if on Android, false otherwise
 */
export const isAndroidDevice = (): boolean => {
  return detectPlatform() === 'android';
};

